import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HandHelping, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { errorCodeOf } from '../../../services/api-error';
import { roundDelegationsService, type HelperDelegation } from '../../../services/round-delegations';
import { apiClient } from '../../../utils/api-client';
import { RoundDelegationContext, type RoundDelegationValue } from './round-delegation-context';
import { credentialOf, findDelegation, forgetDelegation, helperBasePath } from './storage';

/** How often the phone asks whether it is still welcome: a revocation shows within it. */
const RECHECK_MS = 60_000;

type GateState =
  | { kind: 'loading' }
  | { kind: 'ready'; delegation: HelperDelegation }
  /** Over for good: revoked, re-issued, purged. The phone has forgotten it. */
  | { kind: 'gone' }
  /** Could not ask (offline, a server hiccup): kept, and asked again. */
  | { kind: 'failed' };

/**
 * Around the pages a helper's phone works a round on — the team manager's
 * athlete page or the athlete's card, under `/competitions/:slug/rounds/:roundId/aiuto/:token`.
 *
 * It finds the link this phone took (storage.ts), turns the API client's
 * helper's-phone mode on for that round (`apiClient.setRoundDelegation`: the
 * delegation's header on the round's own endpoints, no Bearer anywhere about
 * that competition), asks the server what the phone may do, and only then draws
 * the page — so the page's first requests already go out as the phone's.
 *
 * A phone with no such link is sent to the invitation; one whose link the
 * server no longer knows forgets it and says so. Anything less certain than
 * that — no network, an error — keeps the link: one phone per link means a
 * secret thrown away by mistake cannot be got back.
 */
export default function RoundHelperGate() {
  const { slug, roundId, token } = useParams<{ slug: string; roundId: string; token: string }>();
  const { t } = useTranslation();
  const stored = useMemo(() => findDelegation(token), [token]);
  const [state, setState] = useState<GateState>({ kind: 'loading' });

  const matches = !!stored && stored.roundId === roundId && stored.competitionSlug === slug;

  useEffect(() => {
    if (!stored || !matches) return;
    apiClient.setRoundDelegation({
      credential: credentialOf(stored),
      competitionKeys: [stored.competitionSlug, stored.competitionId],
      roundId: stored.roundId,
    });
    return () => apiClient.setRoundDelegation(null);
  }, [stored, matches]);

  const check = useCallback(async () => {
    if (!stored || !matches) return;
    try {
      const delegation = await roundDelegationsService.current(credentialOf(stored));
      if (delegation.status === 'revoked') {
        forgetDelegation(stored.token);
        setState({ kind: 'gone' });
        return;
      }
      setState({ kind: 'ready', delegation });
    } catch (error) {
      if (errorCodeOf(error) === 'delegation_invalid') {
        forgetDelegation(stored.token);
        setState({ kind: 'gone' });
      } else {
        setState((current) => (current.kind === 'ready' ? current : { kind: 'failed' }));
      }
    }
  }, [stored, matches]);

  useEffect(() => {
    check();
  }, [check]);

  useAutoRefresh(check, { enabled: state.kind === 'ready', intervalMs: RECHECK_MS });

  const value = useMemo<RoundDelegationValue | null>(
    () =>
      stored && state.kind === 'ready'
        ? { stored, delegation: state.delegation, basePath: helperBasePath(stored) }
        : null,
    [stored, state]
  );

  if (!slug || !roundId || !token) return <Navigate to="/not-found" replace />;
  if (!stored || !matches) {
    return <Navigate to={`/competitions/${slug}/rounds/${roundId}/aiuto/${token}`} replace />;
  }

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (state.kind === 'gone' || state.kind === 'failed') {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center" data-testid={`helper-${state.kind}`}>
        <HandHelping className="mx-auto mb-4 size-10 text-muted-foreground" aria-hidden />
        <p className="text-sm">
          {state.kind === 'gone'
            ? t('roundHelp.gone', { name: stored.grantorName })
            : t('roundHelp.failed')}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          {state.kind === 'failed' && (
            <Button size="sm" onClick={() => check()}>
              {t('roundHelp.retry')}
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <Link to={`/competitions/${slug}`}>{t('roundHelp.toCompetition')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <RoundDelegationContext.Provider value={value}>
      <Outlet />
    </RoundDelegationContext.Provider>
  );
}
